import { useEffect, useRef } from "react"

import { LifecycleEnum } from "@boundless/bl-api-client/customer/models"
import { useCustomerAppDocSignUpdate } from "@boundless/bl-api-client/customer/services"
import { useQueryClient } from "@tanstack/react-query"

import { invalidateApplicationDocumentQueries } from "@/components/applications/sign/invalidateApplicationDocumentQueries"

type UseReconcilePendingApplicationDocumentOptions = {
  companyUuid: string
  applicationUuid: string
  fundingNeedsUuid: string
  isSigned: boolean
  lifecycle: LifecycleEnum
  signerEvidenceInvalid: boolean
}

export function useReconcilePendingApplicationDocument({
  companyUuid,
  applicationUuid,
  fundingNeedsUuid,
  isSigned,
  lifecycle,
  signerEvidenceInvalid,
}: UseReconcilePendingApplicationDocumentOptions) {
  const queryClient = useQueryClient()
  const hasCheckedOnMount = useRef(false)
  const { mutate: reconcileApplicationDocument } = useCustomerAppDocSignUpdate({
    mutation: {
      onSuccess: () => {
        void invalidateApplicationDocumentQueries(queryClient, {
          companyUuid,
          fundingNeedsUuid,
          applicationUuid,
        })
      },
    },
  })

  useEffect(() => {
    if (hasCheckedOnMount.current) {
      return
    }
    hasCheckedOnMount.current = true

    // Email signing may complete while the app is closed. Invalid historical
    // evidence requires support, so only retryable pending documents reconcile.
    if (
      !isSigned &&
      lifecycle === LifecycleEnum.pending &&
      !signerEvidenceInvalid
    ) {
      reconcileApplicationDocument({ companyUuid, fundingNeedsUuid })
    }
  }, [
    companyUuid,
    fundingNeedsUuid,
    isSigned,
    lifecycle,
    reconcileApplicationDocument,
    signerEvidenceInvalid,
  ])
}
